import type {DateFormats} from '@innobile/tablemodule';
import type {DateFormatKey, DateFormatProfile} from './date-format';

/**
 * Display patterns in moment tokens. Display only — request params and parse formats keep their
 * own constants, and must never be derived from these. `dateTimeSeconds` is the collision popups'
 * alone: nothing else shows seconds.
 */
export const DATE_FORMATS: Record<DateFormatProfile, Record<DateFormatKey, string>> = {
  default: {
    date: 'YYYY.MM.DD',
    time: 'HH:mm',
    dateTime: 'YYYY.MM.DD HH:mm',
    dateTimeSeconds: 'YYYY.MM.DD HH:mm:ss',
    shortDate: 'MM.DD',
    shortDateTime: 'MM.DD HH:mm',
    shortDateHour: 'MM.DD HH',
    hour: 'HH',
    dayOfMonth: 'DD',
    monthYear: 'YYYY.MM',
  },
  us: {
    date: 'MMM D YYYY',
    time: 'h:mm a',
    dateTime: 'MMM D YYYY h:mm a',
    dateTimeSeconds: 'MMM D YYYY h:mm:ss a',
    shortDate: 'MMM D',
    shortDateTime: 'MMM D h:mm a',
    shortDateHour: 'MMM D h a',
    hour: 'h a',
    dayOfMonth: 'D',
    monthYear: 'MMM YYYY',
  },
};

/** The key that drops the year from a full one: what the US format shows inside the current year. */
export const WITHOUT_YEAR: Readonly<Record<'date' | 'dateTime', DateFormatKey>> = {
  date: 'shortDate',
  dateTime: 'shortDateTime',
};

/**
 * The same patterns in amCharts tokens (`yyyy`, `d` for the day of month — amCharts' `D` is the
 * day of the year). amCharts prints `a` as uppercase `PM` unless the chart's language overrides it.
 */
export const CHART_DATE_FORMATS: Record<DateFormatProfile, Record<DateFormatKey, string>> = {
  default: {
    date: 'yyyy.MM.dd',
    time: 'HH:mm',
    dateTime: 'yyyy.MM.dd HH:mm',
    dateTimeSeconds: 'yyyy.MM.dd HH:mm:ss',
    shortDate: 'MM.dd',
    shortDateTime: 'MM.dd HH:mm',
    shortDateHour: 'MM.dd HH',
    hour: 'HH',
    dayOfMonth: 'dd',
    monthYear: 'yyyy.MM',
  },
  us: {
    date: 'MMM d yyyy',
    time: 'h:mm a',
    dateTime: 'MMM d yyyy h:mm a',
    dateTimeSeconds: 'MMM d yyyy h:mm:ss a',
    shortDate: 'MMM d',
    shortDateTime: 'MMM d h:mm a',
    shortDateHour: 'MMM d h a',
    hour: 'h a',
    dayOfMonth: 'd',
    monthYear: 'MMM yyyy',
  },
};

/**
 * The same patterns in Luxon tokens, for tablemodule's date cells (`TableDateSettings`). They read
 * like the amCharts ones but are a different syntax, so they don't share a constant. tablemodule
 * prints Luxon's `a` lowercase (`7:20 pm`), as moment does.
 */
export const TABLE_DATE_FORMATS: Record<DateFormatProfile, DateFormats> = {
  default: {
    date: 'yyyy.MM.dd',
    time: 'HH:mm',
    dateTime: 'yyyy.MM.dd HH:mm',
    dateTimeSeconds: 'yyyy.MM.dd HH:mm:ss',
    shortDate: 'MM.dd',
    shortDateTime: 'MM.dd HH:mm',
    shortDateHour: 'MM.dd HH',
    hour: 'HH',
    dayOfMonth: 'dd',
    monthYear: 'yyyy.MM',
  },
  us: {
    date: 'MMM d yyyy',
    time: 'h:mm a',
    dateTime: 'MMM d yyyy h:mm a',
    dateTimeSeconds: 'MMM d yyyy h:mm:ss a',
    shortDate: 'MMM d',
    shortDateTime: 'MMM d h:mm a',
    shortDateHour: 'MMM d h a',
    hour: 'h a',
    dayOfMonth: 'd',
    monthYear: 'MMM yyyy',
  },
};

/**
 * period-selector9's date fields. The US format drops the year inside the current year, so a
 * selector does not grow wide; a date in another year gets it back after a comma.
 */
export const PERIOD_DATE_FORMATS = {
  default: 'YYYY.MM.DD',
  usCurrentYear: 'MMM D',
  usOtherYear: 'MMM D, YYYY',
} as const;

/**
 * The 50 states and DC: tzdb's canonical `US` zones (`zone.tab`) plus their `backward` aliases,
 * since a company may carry either. Territories (Puerto Rico, Guam, …) are deliberately absent.
 */
export const US_TIMEZONES: ReadonlySet<string> = new Set([
  // Eastern
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  // Central
  'America/Chicago',
  'America/Menominee',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  // Mountain
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  // Pacific
  'America/Los_Angeles',
  // Alaska
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  // Hawaii–Aleutian
  'America/Adak',
  'Pacific/Honolulu',
  // Aliases
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Alaska',
  'US/Hawaii',
  'US/Arizona',
  'US/East-Indiana',
  'US/Indiana-Starke',
  'US/Michigan',
  'US/Aleutian',
  'America/Indianapolis',
  'America/Fort_Wayne',
  'America/Knox_IN',
  'America/Louisville',
  'America/Atka',
  'America/Shiprock',
  'Navajo',
]);
